import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { get, isEqual } from 'lodash';
import { CSpinner } from '@coreui/react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import GeneralLayoutHeader from './GeneralLayoutHeader';
import GeneralLayoutSideMenu, { SideBarState } from './GeneralLayoutSideMenu';
import GeneralLayoutContent from './GeneralLayoutContent';
import GeneralLayourFooter from './GeneralLayoutFooter';
import { selectUserProfile } from '../store/selectors/profile.selectors';
import {
  selectJourneyConfig,
  selectJourneyConfigChatEnabled,
  selectJourneyConfigError,
  selectJourneyConfigLoaded,
  selectJourneyConfigLoading,
} from '../store/selectors/journey.selectors';
import { GetActiveConfig } from '../customization/actions/journey';
import { JOURNEY_AGENT_LABEL_PATH, JOURNEY_TASK_LABEL_PATH } from '../customization/constants';
import { ConfigContext } from '../shared/contexts/journeyConfig.context';
import { getSelectedOrgId } from '../shared/constants/orgHierarchy.constants';
import { LoadOrgList, LoadSelectedOrg } from '../store/actions/orgHierarchy.action';
import { selectOrgHierarchyActiveNode, selectOrgHierarchySelectedOrg } from '../store/selectors/orgHierarchy.selectors';
import { selectUserOrganization } from '../store/selectors/store.selectors';
import { hasCustomizationEnabledTag, isProOrg } from '../shared/utils/organization-utils';
import { UserRoles } from '../shared/models/user';
import { selectChatState } from '../chat/selectors/chat.selector';
import { ConnectChat, DisconnectChat } from '../chat/actions/chat.action';

const NON_PRO_LABELS = {
  agent: 'Technician',
  task: 'Task',
};

const GeneralLayout = () => {
  const [showMenu, setShowMenu] = useState<SideBarState>(false);
  const [configLabels, setConfigLabels] = useState({
    agent: 'Technician',
    task: 'Job'
  });

  const dispatch = useDispatch();
  const profile = useSelector(selectUserProfile);
  const config = useSelector(selectJourneyConfig);
  const isConfigLoading = useSelector(selectJourneyConfigLoading);
  const isConfigLoaded = useSelector(selectJourneyConfigLoaded);
  const configError = useSelector(selectJourneyConfigError);
  const activeNode = useSelector(selectOrgHierarchyActiveNode);
  const org = useSelector(selectUserOrganization);
  const selectedOrg = useSelector(selectOrgHierarchySelectedOrg);
  const chatEnabled = useSelector(selectJourneyConfigChatEnabled);
  const chatState = useSelector(selectChatState);
  const { enableChatModule } = useFlags();

  useEffect(() => {
    if (enableChatModule && chatEnabled && profile?.orgId && !chatState?.connected && !chatState?.connecting) {
      dispatch(new ConnectChat());
    }
    return () => {
      dispatch(new DisconnectChat());
    };
  }, [enableChatModule, chatEnabled, profile?.orgId]);

  useEffect(() => {
    if (selectedOrg?._id && profile && org?._id) {
      const isAdminOrManager = profile.roles?.includes(UserRoles.Admin) || profile.roles?.includes(UserRoles.Manager);
      const canCreateConfig = isAdminOrManager && (isProOrg(org) || hasCustomizationEnabledTag(org));
      dispatch(new GetActiveConfig(org?._id, canCreateConfig, isProOrg(org)));
    }
  }, [selectedOrg, profile?.orgId, org?._id]);

  useEffect(() => {
    if (org && !isProOrg(org)) {
      setConfigLabels(NON_PRO_LABELS);
    }
  }, [org]);

  useEffect(() => {
    if (config) {
      const newConfigLabels = {
        agent: get(config, JOURNEY_AGENT_LABEL_PATH) || 'Technician',
        task: get(config, JOURNEY_TASK_LABEL_PATH) || 'Job'
      };
      if (!isEqual(newConfigLabels, configLabels)) {
        setConfigLabels(newConfigLabels)
      }
    }
  }, [config]);

  useEffect(() => {
    if (org && !activeNode && !selectedOrg) {
      let activeOrg;
      try {
        activeOrg = localStorage.getItem(getSelectedOrgId(profile.id, org._id));
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log(e);
      }
      dispatch(new LoadSelectedOrg(+activeOrg || org?._id));
    }
  }, [org, activeNode, selectedOrg]);

  useEffect(() => {
    dispatch(new LoadOrgList());
  }, []);

  // Render once the config fetch has settled, whether it succeeded (even with
  // an empty config) or failed - otherwise a rejected/erroring call would leave
  // the spinner up forever, since `loaded` never flips to true on error.
  const configLoadingComplete = !isConfigLoading && (isConfigLoaded || !!configError);

  return (
    <ConfigContext.Provider value={configLabels} >
      { !configLoadingComplete &&
        <div className="c-app c-default-layout flex-row align-items-center justify-content-center">
          <CSpinner
            className="primary-color"
            style={{width:'3rem', height:'3rem'}}
          />
        </div>
      }
      {
        configLoadingComplete && (
          <div className="c-app c-default-layout">
            <GeneralLayoutSideMenu showMenu={showMenu} onMenuToggle={setShowMenu} />
            <div className="c-wrapper">
              <GeneralLayoutHeader
                showMenu={showMenu}
                onMenuToggle={setShowMenu}
              />
              <div className="c-body">
                <GeneralLayoutContent />
              </div>
              <GeneralLayourFooter/>
            </div>
          </div>
        )
      }
    </ConfigContext.Provider>
  );
};

export default GeneralLayout;
